import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import TriggerFieldInputs from "./TriggerFieldInputs";
import { parseAllowedValues } from "./parseAllowedValues";
import {
  PlayArrow as PlayArrowIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import ReactFlow, { // eslint-disable-line @typescript-eslint/no-unused-vars
  Controls, // eslint-disable-line @typescript-eslint/no-unused-vars
  Background, // eslint-disable-line @typescript-eslint/no-unused-vars
  Panel, // eslint-disable-line @typescript-eslint/no-unused-vars
  useNodesState,
  useEdgesState,
  MarkerType,
  Node,
  Edge,
  OnNodesChange, // eslint-disable-line @typescript-eslint/no-unused-vars
  OnEdgesChange, // eslint-disable-line @typescript-eslint/no-unused-vars
} from "reactflow";

import {
  getWorkflow,
  runWorkflow,
  listWorkflowRuns,
  getWorkflowRunDetails,
  resumeWorkflowRun,
  startOAuthConnect,
} from "../../api/agentApi";
import WorkflowNode from "./WorkflowNode";
import { WorkflowNodeData } from "./WorkflowNode";
import {
  getLayoutedElements,
  loadWorkflowNodesWithTriggers,
} from "./workflowUtils";
import {
  Workflow,
  WorkflowRun,
  WorkflowRunDetails,
} from "./WorkflowTypes";
import { useFeatureFlags } from "../../context/FeatureFlagContext";
import WorkflowRunHistorySidebar, { statusColor, formatDate } from "./WorkflowRunHistorySidebar";
import WorkflowRunTimeline from "../WorkflowRunTimeline";
import WorkflowVisualization from "./WorkflowVisualization";
import ReactMarkdown from "react-markdown";

const nodeTypes = { muiNode: WorkflowNode }; // eslint-disable-line @typescript-eslint/no-unused-vars

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const normalizeRunStatus = (status?: string | null): string => {
  if (status === "success") return "completed";
  if (status === "completed") return "completed";
  if (status === "failed" || status === "error") return "failed";
  if (status === "running") return "running";
  if (status === "queued") return "ready";
  if (status === "paused" || status === "auth_required" || status === "waiting_for_input" || status === "waiting_for_approval") return "paused";
  if (status === "skipped") return "skipped";
  return "ready";
};

type WorkflowAuthRequiredSignal = {
  type?: string;
  status?: string;
  reason?: string;
  provider?: string;
  required_env?: string;
  tool_name?: string;
  tool_id?: string;
  step_id?: string;
  workflow_id?: string;
  workflow_version?: number;
  workflow_run_number?: number;
  scopes?: string[];
  message?: string;
};

const isWorkflowAuthSignal = (value: unknown): value is WorkflowAuthRequiredSignal => {
  if (!value || typeof value !== "object") return false;
  const signal = value as WorkflowAuthRequiredSignal;
  return signal.type === "auth_required" || signal.status === "auth_required";
};

const findWorkflowAuthRequired = (runDetails: WorkflowRunDetails | null): WorkflowAuthRequiredSignal | null => {
  if (!runDetails) return null;

  const checkpointSignal = (runDetails as any)?.checkpoint?.auth_required;
  if (isWorkflowAuthSignal(checkpointSignal)) return checkpointSignal;

  const stepResults = (runDetails as any)?.step_results;
  if (stepResults && typeof stepResults === "object") {
    for (const stepResult of Object.values(stepResults)) {
      const signal = (stepResult as any)?.auth_required;
      if (isWorkflowAuthSignal(signal)) return signal;
    }
  }

  const stepsLog = (runDetails as any)?.execution_logs?.steps;
  if (stepsLog && typeof stepsLog === "object") {
    for (const stepLog of Object.values(stepsLog)) {
      const signal = (stepLog as any)?.auth_required;
      if (isWorkflowAuthSignal(signal)) return signal;
    }
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Right Artifacts Panel (placeholder)
// ─────────────────────────────────────────────────────────────────────────────

const ArtifactsPanel: React.FC<{ runDetails: WorkflowRunDetails | null }> = ({
  runDetails,
}) => {
  return (
    <Box
      sx={{
        width: "260px",
        minWidth: "260px",
        borderLeft: "1px solid rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Artifacts
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {!runDetails ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            Select a run from the history to view its details.
          </Typography>
        ) : (
          <>
            <AutoAwesomeIcon sx={{ fontSize: 40, color: "rgba(0,0,0,0.15)" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Artifacts panel coming soon.
            </Typography>
            {runDetails.final_output && (
              <Box sx={{ mt: 2, width: "100%" }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Final Output
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    bgcolor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    fontSize: "0.78rem",
                    color: "#334155",
                    wordBreak: "break-word",
                    maxHeight: "300px",
                    overflowY: "auto",
                    lineHeight: 1.6,
                    "& p": { m: 0, mb: 0.5 },
                    "& p:last-child": { mb: 0 },
                    "& ul, & ol": { pl: 2, m: 0, mb: 0.5 },
                    "& li": { mb: 0.25 },
                    "& code": { bgcolor: "rgba(0,0,0,0.06)", px: 0.4, py: 0.1, borderRadius: "3px", fontFamily: "monospace", fontSize: "0.72rem" },
                    "& pre": { bgcolor: "rgba(0,0,0,0.05)", p: 0.75, borderRadius: "4px", overflowX: "auto", m: 0, mb: 0.5 },
                    "& pre code": { bgcolor: "transparent", p: 0 },
                    "& h1, & h2, & h3, & h4": { m: 0, mb: 0.5, fontWeight: 700 },
                    "& strong": { fontWeight: 700 },
                    "& blockquote": { borderLeft: "3px solid #CBD5E1", pl: 1, ml: 0, color: "#64748b" },
                    "& a": { color: "#4F6AF0" },
                  }}
                >
                  {typeof runDetails.final_output === "string" ? (
                    <ReactMarkdown>{runDetails.final_output}</ReactMarkdown>
                  ) : (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {JSON.stringify(runDetails.final_output, null, 2)}
                    </pre>
                  )}
                </Paper>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main WorkflowRunPage
// ─────────────────────────────────────────────────────────────────────────────

export default function WorkflowRunPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  useFeatureFlags(); // keep context subscription; isEnabled no longer needed here

  // ── Workflow data ──────────────────────────────────────────────────────────
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  // ── React Flow nodes / edges ───────────────────────────────────────────────
  const [baseNodes, setBaseNodes] = useState<Node[]>([]);
  const [baseEdges, setBaseEdges] = useState<Edge[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Node properties drawer
  const [selectedElement, setSelectedElement] = useState< // eslint-disable-line @typescript-eslint/no-unused-vars
    Node<WorkflowNodeData> | Edge | null
  >(null);
  const [nodeDrawerOpen, setNodeDrawerOpen] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  // ── Run history ────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [runsPage, setRunsPage] = useState(1);
  const PAGE_SIZE = 20;
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const [isLoadingMoreRuns, setIsLoadingMoreRuns] = useState(false);
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const runsLengthRef = useRef(0); // stable ref to avoid stale closures in fetchRuns
  // Tracks whether we've already auto-selected a run on initial page load.
  // Once true, subsequent runsRefreshToken bumps (after run completion) won't
  // override whatever the user or a new submission has selected.
  const hasAutoSelected = useRef(false);

  // ── Selected run & details ─────────────────────────────────────────────────
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [runDetails, setRunDetails] = useState<WorkflowRunDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const runDetailsSignatureRef = useRef<string | null>(null);

  // ── Run workflow dialog ────────────────────────────────────────────────────
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  // triggerInputs: keyed by schema variable name (or "input" for free-form fallback)
  const [triggerInputs, setTriggerInputs] = useState<Record<string, string>>({});
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [runSubmitting, setRunSubmitting] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [oauthStatusMessage, setOauthStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    text: string;
  } | null>(null);

  // Derived: trigger_schema from the loaded workflow DAG ([] when not present)
  const triggerSchema: Array<{ name: string; type?: string; required?: boolean; description?: string }> =
    useMemo(() => (workflow?.dag_definition as any)?.trigger_schema ?? [], [workflow]);

  // True when all required schema fields have a non-empty value (or there are no required fields)
  const canSubmit = useMemo(() => {
    if (triggerSchema.length === 0) return true; // free-form — always submittable
    return triggerSchema
      .filter((f: any) => f.required)
      .every((f: any) => {
        const val = triggerInputs[f.name] ?? "";
        if (f.type === "boolean") return true; // checkbox always has a value
        if (f.type === "number" || f.type === "integer") {
          return val.trim().length > 0 && !isNaN(Number(val));
        }
        return val.trim().length > 0;
      });
  }, [triggerSchema, triggerInputs]);

  // ── Center tab (0 = Timeline, 1 = Graph) ──────────────────────────────────
  const [centerTab, setCenterTab] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────
  // Load workflow & build base nodes
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!workflowId) return;

    const load = async () => {
      setWorkflowLoading(true);
      setWorkflowError(null);
      try {
        const data = await getWorkflow(workflowId);
        setWorkflow(data);

        const { nodes: rawNodes, edges: rawEdges } =
          await loadWorkflowNodesWithTriggers(
            data,
            "default",
            { type: MarkerType.ArrowClosed },
            false
          );

        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(rawNodes, rawEdges);

        // Restore saved positions
        const savedKey = `wf-${workflowId}-${data.version ?? 1}`;
        const savedLayout = localStorage.getItem(savedKey);
        let finalNodes = layoutedNodes;
        if (savedLayout) {
          try {
            const parsed = JSON.parse(savedLayout);
            finalNodes = layoutedNodes.map((n) => {
              const pos = parsed[n.id];
              if (pos && typeof pos === "object" && "x" in pos && "y" in pos) {
                return { ...n, position: pos };
              }
              return n;
            });
          } catch { /* ignore */ }
        }

        setBaseNodes(finalNodes);
        setBaseEdges(layoutedEdges);
        setNodes(finalNodes);
        setEdges(layoutedEdges);
      } catch (err) {
        setWorkflowError((err as Error).message);
      } finally {
        setWorkflowLoading(false);
      }
    };

    load();
  }, [workflowId, setNodes, setEdges]);

  // ─────────────────────────────────────────────────────────────────────────
  // Derived: apply run status overlays to nodes
  // ─────────────────────────────────────────────────────────────────────────
  const runNodes = useMemo(() => {
    if (!runDetails) {
      return baseNodes.map((n) => ({
        ...n,
        data: { ...n.data, show_status: false, status: undefined },
      }));
    }

    const stepsLog =
      (runDetails.execution_logs as any)?.steps &&
      typeof (runDetails.execution_logs as any).steps === "object"
        ? (runDetails.execution_logs as any).steps
        : {};
    const stepResults =
      runDetails.step_results && typeof runDetails.step_results === "object"
        ? runDetails.step_results
        : {};

    const isTerminal =
      runDetails.status === "completed" || runDetails.status === "failed";

    // Build set of terminal loop IDs (success or failed) so body steps with
    // stale statuses can be resolved — the body step was already handled for
    // the loop to have moved past it.
    const terminalLoopIds = new Set<string>();
    for (const [key, log] of Object.entries(stepsLog)) {
      const l = log as any;
      if (l.step_type === "loop" && (l.status === "success" || l.status === "completed" || l.status === "failed" || l.status === "error")) {
        terminalLoopIds.add(key);
      }
    }

    return baseNodes.map((node) => {
      if (node.data.type === "trigger") {
        return { ...node, data: { ...node.data, show_status: false, status: undefined } };
      }

      const logEntry = stepsLog?.[node.id];
      const logStatus = logEntry?.status;
      const resultStatus = stepResults?.[node.id]?.status;
      let resolved = normalizeRunStatus(resultStatus || logStatus);

      // If this step's parent loop is terminal, mark body step as completed
      // (the failure belongs to the loop or a different body step)
      const parentLoop = logEntry?.parent_loop || (stepResults?.[node.id] as any)?.parent_loop;
      if (parentLoop && terminalLoopIds.has(parentLoop) && resolved !== "failed") {
        resolved = "completed";
      }

      if (isTerminal && resolved === "ready") resolved = "skipped";

      return {
        ...node,
        data: { ...node.data, show_status: true, status: resolved },
      };
    });
  }, [baseNodes, runDetails]);

  // Keep flow nodes in sync with runNodes
  useEffect(() => {
    setNodes(runNodes);
  }, [runNodes, setNodes]);

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch run history
  // ─────────────────────────────────────────────────────────────────────────
  const sortRunsByTime = (list: WorkflowRun[]) =>
    [...list].sort((a, b) => {
      const ta = new Date((a.started_at ?? a.created_at) || 0).getTime();
      const tb = new Date((b.started_at ?? b.created_at) || 0).getTime();
      return tb - ta;
    });

  const fetchRuns = useCallback(
    async (page = 1, append = false, autoSelect = false) => {
      if (!workflowId) return;

      try {
        const data = await listWorkflowRuns(workflowId, {
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        });

        const list: WorkflowRun[] = Array.isArray((data as any).runs)
          ? sortRunsByTime((data as any).runs)
          : Array.isArray(data)
          ? sortRunsByTime(data as WorkflowRun[])
          : [];
        const total: number = (data as any).total ?? list.length;

        setRuns((prev) => {
          const next = append ? [...prev, ...list] : list;
          runsLengthRef.current = next.length;
          return next;
        });
        setTotalRuns(total);
        setRunsPage(page);
        setHasMoreRuns(runsLengthRef.current < total);

        // ── Auto-select on initial page load ───────────────────────────────
        // Priority 1: any run that is actively running or queued.
        // Priority 2: the most recent run (index 0 after sort).
        // Only fires once — hasAutoSelected prevents subsequent refreshes
        // (e.g. after a run completes) from overriding user / in-flight selection.
        if (autoSelect && !hasAutoSelected.current && list.length > 0) {
          hasAutoSelected.current = true;
          const activeStatuses = new Set(["running", "queued", "started"]);
          const activeRun = list.find((r) => activeStatuses.has(r.status ?? ""));
          const toSelect = activeRun ?? list[0];
          runDetailsSignatureRef.current = null;
          setRunDetails(null);
          setSelectedRun(toSelect);
        }
      } catch (err) {
        console.error("Failed to fetch workflow runs:", err);
      } finally {
        setIsLoadingMoreRuns(false);
      }
    },
    [workflowId] // ← no runs.length; use the ref instead
  );

  // Load history once on mount (autoSelect=true), and again when runsRefreshToken
  // changes after a run completes (autoSelect=false — don't disturb current selection).
  useEffect(() => {
    // runsRefreshToken === 0 is the initial mount; > 0 is a post-completion refresh.
    fetchRuns(1, false, runsRefreshToken === 0);
  }, [workflowId, runsRefreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── NO list-level polling ──────────────────────────────────────────────────
  // History is refreshed only when a run reaches a terminal state (see below).
  // Polling is done on the individual run-details endpoint instead.

  const handleLoadMoreRuns = () => {
    if (isLoadingMoreRuns || !hasMoreRuns) return;
    setIsLoadingMoreRuns(true);
    fetchRuns(runsPage + 1, true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch run details + polling
  // ─────────────────────────────────────────────────────────────────────────
  const fetchRunDetails = useCallback(
    async (
      version: number,
      runNumber: number | string,
      opts?: { silent?: boolean }
    ): Promise<WorkflowRunDetails | null> => {
      if (!workflowId) return null;
      const showLoading = !opts?.silent;
      if (showLoading) setDetailsLoading(true);

      try {
        const data = await getWorkflowRunDetails(workflowId, version, runNumber);
        const sig = JSON.stringify({
          status: data.status,
          final_output: data.final_output,
          step_results: data.step_results,
          execution_logs: data.execution_logs,
          checkpoint: data.checkpoint,
        });

        if (!opts?.silent || runDetailsSignatureRef.current !== sig) {
          runDetailsSignatureRef.current = sig;
          setRunDetails(data);
        }
        return data;
      } catch (err) {
        if (showLoading) console.error("Failed to fetch run details:", err);
        return null;
      } finally {
        if (showLoading) setDetailsLoading(false);
      }
    },
    [workflowId]
  );

  // Auto-poll selected run while it's active
  useEffect(() => {
    if (!selectedRun) return;
    const runNum = selectedRun.run_number ?? (selectedRun as any).id;
    const version = selectedRun.workflow_version ?? selectedRun.version ?? 1;
    if (runNum == null) return;

    let active = true;
    let intervalId: number | undefined;

    const isTerminal = (s?: string | null) =>
      s === "completed" || s === "failed" || s === "cancelled" || s === "error";

    const poll = async () => {
      const initial = await fetchRunDetails(version, runNum);
      if (!active) return;
      if (isTerminal(initial?.status)) {
        // Already done — refresh history so sidebar is accurate
        setRunsRefreshToken((t) => t + 1);
        return;
      }

      intervalId = window.setInterval(async () => {
        const next = await fetchRunDetails(version, runNum, { silent: true });
        if (!active) return;
        if (isTerminal(next?.status) && intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
          // Refresh history list now that the run has reached a terminal state
          setRunsRefreshToken((t) => t + 1);
        }
      }, 3000);
    };

    poll();

    return () => {
      active = false;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [selectedRun, fetchRunDetails]);

  const handleSelectRun = (run: WorkflowRun) => {
    runDetailsSignatureRef.current = null;
    setSelectedRun(run);
    setRunDetails(null);
    setOauthStatusMessage(null);
  };

  const workflowAuthRequired = useMemo(
    () => findWorkflowAuthRequired(runDetails),
    [runDetails]
  );

  const selectedRunNumber =
    selectedRun?.run_number ??
    (runDetails as any)?.run_number ??
    (selectedRun as any)?.id;

  const selectedRunVersion =
    (runDetails as any)?.workflow_version ??
    selectedRun?.workflow_version ??
    selectedRun?.version ??
    workflow?.version ??
    1;

  const handleWorkflowOAuthConnect = async () => {
    if (!workflowId || !workflowAuthRequired?.provider || selectedRunNumber == null) {
      setOauthStatusMessage({
        severity: "error",
        text: "This workflow pause is missing provider or run metadata. Refresh the page and try again.",
      });
      return;
    }

    setOauthConnecting(true);
    setOauthStatusMessage(null);
    try {
      const response = await startOAuthConnect({
        provider: workflowAuthRequired.provider,
        required_env: workflowAuthRequired.required_env,
        tool_name: workflowAuthRequired.tool_name,
        scopes: workflowAuthRequired.scopes,
        workflow_id: workflowId,
        workflow_version: workflowAuthRequired.workflow_version ?? selectedRunVersion,
        workflow_run_number: workflowAuthRequired.workflow_run_number ?? selectedRunNumber,
        step_id: workflowAuthRequired.step_id,
        return_to: window.location.href,
      });

      const authUrl = response?.authorization_url;
      if (!authUrl) {
        throw new Error("OAuth provider did not return an authorization URL");
      }

      const popup = window.open(
        authUrl,
        "nextmoca_workflow_oauth_connect",
        "width=640,height=760,menubar=no,toolbar=no,location=no,status=no",
      );
      if (!popup) {
        throw new Error("Popup blocked. Allow popups for Next Moca and try again.");
      }
    } catch (err) {
      setOauthConnecting(false);
      setOauthStatusMessage({
        severity: "error",
        text: (err as Error)?.message || "Could not start OAuth connection.",
      });
    }
  };

  useEffect(() => {
    const onOAuthMessage = (event: MessageEvent) => {
      const payload = event.data || {};
      if (payload.type !== "nextmoca_oauth_complete") return;
      if (!payload.workflow_id || payload.workflow_id !== workflowId) return;

      const version =
        payload.workflow_version ??
        workflowAuthRequired?.workflow_version ??
        selectedRunVersion;
      const runNumber =
        payload.workflow_run_number ??
        workflowAuthRequired?.workflow_run_number ??
        selectedRunNumber;

      setOauthConnecting(false);

      if (payload.status !== "success") {
        setOauthStatusMessage({
          severity: "error",
          text: payload.message || "OAuth connection was not completed.",
        });
        return;
      }

      if (runNumber == null) {
        setOauthStatusMessage({
          severity: "error",
          text: "Credential connected, but this workflow run could not be resumed because the run number was missing.",
        });
        return;
      }

      setOauthStatusMessage({
        severity: "info",
        text: "Credential connected. Resuming workflow...",
      });

      (async () => {
        try {
          await resumeWorkflowRun(payload.workflow_id, version, runNumber);
          setOauthStatusMessage({
            severity: "success",
            text: "Workflow resumed with the connected credential.",
          });
          await fetchRunDetails(version, runNumber, { silent: true });
          setRunsRefreshToken((t) => t + 1);
        } catch (err) {
          setOauthStatusMessage({
            severity: "error",
            text: (err as Error)?.message || "Credential connected, but workflow resume failed.",
          });
        }
      })();
    };

    window.addEventListener("message", onOAuthMessage);
    return () => window.removeEventListener("message", onOAuthMessage);
  }, [
    workflowId,
    workflowAuthRequired,
    selectedRunNumber,
    selectedRunVersion,
    fetchRunDetails,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // Run workflow
  // ─────────────────────────────────────────────────────────────────────────
  const handleRunWorkflow = async () => {
    if (!workflowId) return;
    setRunSubmitting(true);
    setRunStatus(null);
    setOauthStatusMessage(null);
    try {
      // Build input dict from schema fields (or free-form "input" fallback).
      // agentApi.runWorkflow wraps this as body: { input: <dict> }.
      let inputDict: Record<string, any>;
      if (triggerSchema.length > 0) {
        // Schema-driven: coerce each field by its declared type
        inputDict = {};
        for (const field of triggerSchema) {
          const raw = triggerInputs[field.name] ?? "";
          const type = field.type;
          if (type === "number") {
            inputDict[field.name] = parseFloat(raw);
          } else if (type === "integer") {
            inputDict[field.name] = parseInt(raw, 10);
          } else if (type === "boolean") {
            inputDict[field.name] = raw === "true";
          } else if (type === "array" || type === "object") {
            try {
              inputDict[field.name] = JSON.parse(raw);
            } catch {
              setRunStatus(`Invalid JSON for field "${field.name}"`);
              setRunSubmitting(false);
              return;
            }
          } else {
            inputDict[field.name] = raw.trim();
          }
        }
      } else {
        // Free-form fallback: wrap in { input: "..." } or send {}
        const freeText = triggerInputs["input"]?.trim() ?? "";
        inputDict = freeText ? { input: freeText } : {};
      }
      const response: any = await runWorkflow(workflowId, inputDict);
      setRunDialogOpen(false);
      setTriggerInputs({});

      // The POST response contains run_number and version directly —
      // use those to build the new run object immediately without a second
      // list fetch (which can race before the DB row is visible).
      const runNumber: number | undefined = response?.run_number;
      const runVersion: number = response?.version ?? workflow?.version ?? 1;

      if (runNumber != null) {
        const newRun: WorkflowRun = {
          run_number: runNumber,
          workflow_version: runVersion,
          status: "queued",
          started_at: new Date().toISOString(),
        };
        // Prepend to sidebar immediately
        setRuns((prev) => {
          const withNew = [newRun, ...prev.filter((r) =>
            (r.run_number ?? (r as any).id) !== runNumber
          )];
          runsLengthRef.current = withNew.length;
          return withNew;
        });
        // Auto-select → kicks off detail polling via the selectedRun useEffect
        runDetailsSignatureRef.current = null;
        setRunDetails(null);
        setSelectedRun(newRun);
      } else {
        // Fallback: refresh list if response didn't include run_number
        setRunsRefreshToken((t) => t + 1);
      }
    } catch (err) {
      setRunStatus(`Error: ${(err as Error).message}`);
    } finally {
      setRunSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ResizeObserver suppression (React Flow)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: ErrorEvent) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Node position save on drag
  // ─────────────────────────────────────────────────────────────────────────
  const layoutKey = workflowId
    ? `wf-${workflowId}-${workflow?.version ?? 1}`
    : null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node) => {
      if (!layoutKey) return;
      setNodes((currentNodes) => {
        try {
          const existing = localStorage.getItem(layoutKey);
          const parsed = existing ? JSON.parse(existing) : {};
          currentNodes.forEach((n) => {
            parsed[n.id] = n.position;
          });
          localStorage.setItem(layoutKey, JSON.stringify(parsed));
        } catch { /* ignore */ }
        return currentNodes;
      });
    },
    [layoutKey, setNodes]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Run status summary card (above the flow)
  // ─────────────────────────────────────────────────────────────────────────
  const RunStatusBanner: React.FC = () => {
    if (!selectedRun) return null;
    const st = (runDetails?.status ?? selectedRun.status) || "unknown";
    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: statusColor(st),
            }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
            Run #{selectedRun.run_number ?? (selectedRun as any).id} — {st}
          </Typography>
        </Box>
        {detailsLoading && <CircularProgress size={14} />}
        {runDetails?.started_at && (
          <Typography variant="caption" color="text.secondary">
            Started: {formatDate(runDetails.started_at)}
          </Typography>
        )}
        {runDetails?.finished_at && (
          <Typography variant="caption" color="text.secondary">
            Finished: {formatDate(runDetails.finished_at)}
          </Typography>
        )}
        {runDetails?.error_message && (
          <Typography variant="caption" color="error">
            {runDetails.error_message}
          </Typography>
        )}
      </Box>
    );
  };

  const WorkflowOAuthBanner: React.FC = () => {
    if (!workflowAuthRequired && !oauthStatusMessage) return null;

    const providerLabel =
      workflowAuthRequired?.provider
        ? workflowAuthRequired.provider.charAt(0).toUpperCase() + workflowAuthRequired.provider.slice(1)
        : "OAuth";
    const toolLabel = workflowAuthRequired?.tool_name || "workflow tool";
    const severity = oauthStatusMessage?.severity ?? "warning";
    const message =
      oauthStatusMessage?.text ||
      workflowAuthRequired?.message ||
      `${toolLabel} needs ${providerLabel} authorization before this workflow can continue.`;

    return (
      <Alert
        severity={severity}
        icon={<VpnKeyIcon fontSize="inherit" />}
        sx={{
          mx: 2,
          my: 1,
          borderRadius: 2,
          border: "1px solid rgba(184,115,51,0.28)",
          "& .MuiAlert-message": { width: "100%" },
        }}
        action={
          workflowAuthRequired?.provider ? (
            <Button
              color="inherit"
              size="small"
              onClick={handleWorkflowOAuthConnect}
              disabled={oauthConnecting}
              startIcon={oauthConnecting ? <CircularProgress size={14} color="inherit" /> : <VpnKeyIcon />}
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 700 }}
            >
              {oauthConnecting ? "Connecting..." : `Connect ${providerLabel}`}
            </Button>
          ) : undefined
        }
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Workflow authorization required
          </Typography>
          <Typography variant="body2">
            {message}
          </Typography>
          {workflowAuthRequired?.required_env && (
            <Typography variant="caption" color="text.secondary">
              Required credential: {workflowAuthRequired.required_env}
            </Typography>
          )}
        </Box>
      </Alert>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Type-aware trigger field renderer
  // ─────────────────────────────────────────────────────────────────────────
  const renderTriggerField = (field: any) => {
    return (
      <TriggerFieldInputs
        key={field.name}
        field={field}
        value={triggerInputs[field.name] ?? ""}
        onChange={(v) => setTriggerInputs((prev) => ({ ...prev, [field.name]: v }))}
        options={parseAllowedValues(field.description)}
      />
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  if (workflowLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading workflow…</Typography>
      </Box>
    );
  }

  if (workflowError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{workflowError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "87vh", display: "flex", flexDirection: "column" }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Back to dashboard">
            <IconButton
              size="small"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" fontWeight={700} noWrap>
            {workflow?.name ?? "Workflow"}
          </Typography>
          {workflow?.version && (
            <Chip
              label={`v${workflow.version}`}
              size="small"
              sx={{ fontWeight: 500, bgcolor: "#F1F5F9", height: 22 }}
            />
          )}
        </Box>

      </Box>

      {/* ── Three-column body ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          overflow: "hidden",
          position: "relative",
          minHeight: 0,
        }}
      >
        {/* Left — Run History Sidebar */}
        <WorkflowRunHistorySidebar
          isOpen={sidebarOpen}
          runs={runs}
          selectedRunNumber={
            selectedRun?.run_number ?? (selectedRun as any)?.id ?? null
          }
          hasMore={hasMoreRuns}
          isLoadingMore={isLoadingMoreRuns}
          totalRuns={totalRuns}
          onToggleOpen={() => setSidebarOpen(true)}
          onToggleClose={() => setSidebarOpen(false)}
          onSelectRun={handleSelectRun}
          onLoadMore={handleLoadMoreRuns}
          onRefresh={() => { setRunsPage(1); setRuns([]); setRunsRefreshToken(t => t + 1); }}
        />

        {/* Center — Workflow Visualization */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {/* Run status banner */}
          <RunStatusBanner />
          <WorkflowOAuthBanner />

          {/* Action / hint bar — always visible above the canvas */}
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#FAFAFA",
            }}
          >
            <Typography variant="caption" color="text.secondary" noWrap>
              {!selectedRun && runs.length === 0
                ? "No runs yet —"
                : !selectedRun
                ? "← Select a run to visualize it, or"
                : null}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={() => {
                setRunStatus(null);
                setTriggerInputs({});
                setRunDialogOpen(true);
              }}
              sx={{ borderRadius: "99px", textTransform: "none", fontSize: "0.75rem", py: 0.25 }}
            >
              Run Workflow
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/workflow/${workflowId}`)}
              sx={{ borderRadius: "99px", textTransform: "none", fontSize: "0.75rem", py: 0.25 }}
            >
              View Details
            </Button>
          </Box>

          {/* Tabs — Timeline | Graph */}
          <Tabs
            value={centerTab}
            onChange={(_e, v) => setCenterTab(v)}
            sx={{
              minHeight: 36,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              bgcolor: "#FAFAFA",
              "& .MuiTab-root": { minHeight: 36, py: 0, fontSize: "0.78rem", textTransform: "none" },
            }}
          >
            <Tab label="Timeline" />
            <Tab label="Graph" />
          </Tabs>

          {/* Tab panels */}
          {centerTab === 0 && (
            <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0, position: "relative" }}>
              <WorkflowRunTimeline
                runDetails={runDetails}
                workflowName={workflow?.name}
                dagDefinition={workflow?.dag_definition}
              />
            </Box>
          )}

          {centerTab === 1 && (
            <Box sx={{ flex: 1, position: "relative", minHeight: 300, overflow: "hidden" }}>
              <WorkflowVisualization dagDefinition={workflow?.dag_definition} />
            </Box>
          )}
        </Box>

        {/* Right — Artifacts Panel */}
        <ArtifactsPanel runDetails={runDetails} />
      </Box>

      {/* ── Run Workflow Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={runDialogOpen}
        onClose={() => { setRunDialogOpen(false); setTriggerInputs({}); setRunStatus(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Run Workflow</DialogTitle>
        <DialogContent>
          {triggerSchema.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Provide values for the workflow's input variables.
              </Typography>
              {triggerSchema.some((f: any) => f.required) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  * Required fields
                </Typography>
              )}
              {triggerSchema.map(renderTriggerField)}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                This workflow needs no input to run, but you can optionally provide a prompt.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Prompt (optional)"
                value={triggerInputs["input"] ?? ""}
                onChange={(e) =>
                  setTriggerInputs((prev) => ({ ...prev, input: e.target.value }))
                }
                placeholder="e.g. Conduct market research for electric vehicles… (optional)"
                sx={{ mb: 2 }}
              />
            </>
          )}
          {runStatus && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {runStatus}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRunDialogOpen(false); setTriggerInputs({}); setRunStatus(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRunWorkflow}
            disabled={runSubmitting || !canSubmit}
            startIcon={
              runSubmitting ? <CircularProgress size={16} /> : <PlayArrowIcon />
            }
          >
            {runSubmitting ? "Starting…" : "Start Run"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
